import { useRef, useState, useCallback, useEffect } from 'react';

const API = process.env.REACT_APP_API_URL || '';

// Общие параметры RTCPeerConnection (без секретов).
const RTC_CONFIG_BASE = {
  iceCandidatePoolSize: 10,
  iceTransportPolicy: 'all',
  bundlePolicy: 'max-bundle',
  rtcpMuxPolicy: 'require',
};

// Фолбэк, если сервер недоступен — только публичные STUN (без TURN-секретов).
const DEFAULT_ICE_SERVERS = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
];

export function useVoiceChat({ socketRef, roomId }) {
  const [inVoice, setInVoice] = useState(false);
  const [voiceUsers, setVoiceUsers] = useState({});
  const [muted, setMuted] = useState(false);
  const [volumes, setVolumes] = useState({});

  const localStreamRef = useRef(null);
  const peersRef = useRef({});
  const audioRefs = useRef({});
  const inVoiceRef = useRef(false);
  const reconnectAttemptsRef = useRef({});
  // Буфер ICE-кандидатов — на случай если они придут до setRemoteDescription
  const iceCandidateBufferRef = useRef({});
  // volumes в ref, чтобы createPeer не зависел от volumes state
  const volumesRef = useRef({});
  // Флаг идущего negotiation — защита от двойного onnegotiationneeded
  const negotiatingRef = useRef({});
  // Роль в паре (initiator?) по каждому пиру — нужно чтобы ICE-restart делала
  // только одна сторона (иначе оба шлют offer одновременно → glare → разрыв).
  const peerRolesRef = useRef({});
  // Кэш конфигурации ICE-серверов, полученной с сервера (TURN-секреты не в бандле).
  const iceConfigRef = useRef(null);

  const fetchIceConfig = useCallback(async () => {
    if (iceConfigRef.current) return iceConfigRef.current;
    let iceServers = DEFAULT_ICE_SERVERS;
    try {
      const res = await fetch(`${API}/api/ice-servers`);
      const data = await res.json();
      if (Array.isArray(data?.iceServers) && data.iceServers.length) {
        iceServers = data.iceServers;
      }
    } catch (err) {
      console.warn('[voice] Не удалось получить ICE-конфиг с сервера, используем STUN-фолбэк:', err);
    }
    iceConfigRef.current = { ...RTC_CONFIG_BASE, iceServers };
    return iceConfigRef.current;
  }, []);

  const flushIceCandidates = useCallback(async (targetId) => {
    const pc = peersRef.current[targetId];
    const buffer = iceCandidateBufferRef.current[targetId];
    if (!pc || !buffer?.length) return;
    console.log(`[voice] Flushing ${buffer.length} buffered ICE candidates for ${targetId}`);
    for (const candidate of buffer) {
      try {
        await pc.addIceCandidate(new RTCIceCandidate(candidate));
      } catch (err) {
        console.warn(`[voice] Error adding buffered ICE for ${targetId}:`, err);
      }
    }
    iceCandidateBufferRef.current[targetId] = [];
  }, []);

  const restartIceForPeer = useCallback(async (targetId) => {
    const pc = peersRef.current[targetId];
    if (!pc || pc.signalingState !== 'stable') return;
    // ICE-restart инициирует только сторона-initiator — иначе обе создадут offer
    // одновременно и получится glare. Не-initiator ждёт offer от initiator.
    if (!peerRolesRef.current[targetId]) return;
    try {
      console.log(`[voice] 🔄 Restarting ICE for ${targetId}`);
      const offer = await pc.createOffer({ iceRestart: true, offerToReceiveAudio: true });
      await pc.setLocalDescription(offer);
      socketRef.current?.emit('webrtc_offer', {
        roomId, targetId,
        offer: { type: offer.type, sdp: offer.sdp },
      });
    } catch (err) {
      console.error(`[voice] ICE restart error for ${targetId}:`, err);
    }
  }, [roomId, socketRef]);

  const createPeer = useCallback((targetId, initiator) => {
    // ─── RACE CONDITION FIX ───────────────────────────────────────────────────
    // Если уже есть живой peer (connecting/connected) — не пересоздаём его.
    // Это предотвращает ситуацию: onOffer создал peer как non-initiator,
    // а onExistingVoiceUsers через секунду пересоздаёт его как initiator.
    const existing = peersRef.current[targetId];
    if (existing) {
      const cs = existing.connectionState;
      const ss = existing.signalingState;
      // Skip if the peer is live or mid-negotiation — don't disrupt an in-flight handshake
      if (
        cs === 'connecting' || cs === 'connected' || cs === 'new' ||
        ss === 'have-local-offer' || ss === 'have-remote-offer'
      ) {
        console.log(`[voice] createPeer(${targetId}) skipped — cs=${cs} ss=${ss}`);
        return existing;
      }
      existing.close();
      delete peersRef.current[targetId];
    }
    // ─────────────────────────────────────────────────────────────────────────

    console.log(`[voice] createPeer(${targetId}, initiator=${initiator})`);
    iceCandidateBufferRef.current[targetId] = [];
    negotiatingRef.current[targetId] = false;
    peerRolesRef.current[targetId] = !!initiator;

    const pc = new RTCPeerConnection(iceConfigRef.current || { ...RTC_CONFIG_BASE, iceServers: DEFAULT_ICE_SERVERS });

    pc.onicecandidate = (event) => {
      if (event.candidate) {
        console.log(`[voice] ICE candidate → ${targetId} | type: ${event.candidate.type}`);
        socketRef.current?.emit('webrtc_ice', { roomId, targetId, candidate: event.candidate });
      } else {
        console.log(`[voice] ICE gathering complete for ${targetId}`);
      }
    };

    pc.oniceconnectionstatechange = () => {
      const state = pc.iceConnectionState;
      console.log(`[voice] ICE state for ${targetId}: ${state}`);
      if (state === 'connected' || state === 'completed') {
        console.log(`[voice] ✅ ICE connected to ${targetId}`);
        reconnectAttemptsRef.current[targetId] = 0;
      } else if (state === 'failed') {
        console.warn(`[voice] ❌ ICE failed for ${targetId}`);
        if (!inVoiceRef.current) return;
        if (initiator) {
          const attempts = (reconnectAttemptsRef.current[targetId] || 0) + 1;
          reconnectAttemptsRef.current[targetId] = attempts;
          if (attempts <= 3) {
            console.log(`[voice] Retry ${attempts}/3 for ${targetId} via ICE restart`);
            setTimeout(() => restartIceForPeer(targetId), 1500 * attempts);
          } else {
            // ICE-restart не помог — просим сервер пересобрать пару целиком (обе стороны).
            console.warn(`[voice] 💀 Requesting server re-pair for ${targetId}`);
            socketRef.current?.emit('voice_repair', { roomId, peerId: targetId });
          }
        } else {
          // Не-initiator: ждём восстановления от initiator; если через 8с всё ещё failed
          // (например, ассиметричный сбой) — просим сервер пересобрать пару как backstop.
          setTimeout(() => {
            if (inVoiceRef.current && peersRef.current[targetId]?.iceConnectionState === 'failed') {
              console.warn(`[voice] 💀 (receiver) Requesting server re-pair for ${targetId}`);
              socketRef.current?.emit('voice_repair', { roomId, peerId: targetId });
            }
          }, 8000);
        }
      } else if (state === 'disconnected') {
        console.log(`[voice] ⚠️ ICE disconnected from ${targetId}, scheduling restart...`);
        setTimeout(() => {
          if (pc.iceConnectionState === 'disconnected') restartIceForPeer(targetId);
        }, 3000);
      }
    };

    pc.onconnectionstatechange = () => {
      const state = pc.connectionState;
      console.log(`[voice] Connection state for ${targetId}: ${state}`);
      if (state === 'connected') {
        console.log(`[voice] 🎤 Audio connected to ${targetId}!`);
      }
    };

    pc.onicegatheringstatechange = () => {
      console.log(`[voice] ICE gathering state for ${targetId}: ${pc.iceGatheringState}`);
    };

    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => {
        pc.addTrack(track, localStreamRef.current);
        console.log(`[voice] Added ${track.kind} track to peer ${targetId}`);
      });
    } else {
      console.warn(`[voice] No local stream for ${targetId}`);
    }

    pc.ontrack = (event) => {
      console.log(`[voice] 🎧 ontrack от ${targetId} — аудио получено!`);
      if (!audioRefs.current[targetId]) {
        const audio = new Audio();
        audio.autoplay = true;
        audio.volume = volumesRef.current[targetId] ?? 1;
        audioRefs.current[targetId] = audio;
      }
      audioRefs.current[targetId].srcObject = event.streams[0];
      audioRefs.current[targetId].play().catch(e => {
        console.warn(`[voice] Auto-play blocked for ${targetId}:`, e);
        const playOnInteraction = () => {
          audioRefs.current[targetId]?.play().catch(console.warn);
          document.removeEventListener('click', playOnInteraction);
          document.removeEventListener('keydown', playOnInteraction);
        };
        document.addEventListener('click', playOnInteraction);
        document.addEventListener('keydown', playOnInteraction);
      });
    };

    if (initiator) {
      // ─── DOUBLE-NEGOTIATION FIX ───────────────────────────────────────────
      // negotiatingRef предотвращает параллельный запуск двух createOffer
      pc.onnegotiationneeded = async () => {
        if (negotiatingRef.current[targetId]) {
          console.log(`[voice] onnegotiationneeded: already negotiating ${targetId}, skip`);
          return;
        }
        if (pc.signalingState !== 'stable') {
          console.log(`[voice] onnegotiationneeded: state=${pc.signalingState}, skip`);
          return;
        }
        negotiatingRef.current[targetId] = true;
        try {
          console.log(`[voice] Creating offer for ${targetId}`);
          const offer = await pc.createOffer({ offerToReceiveAudio: true, offerToReceiveVideo: false });
          // Проверяем снова после await — состояние могло измениться
          if (pc.signalingState !== 'stable') {
            console.log(`[voice] State changed during createOffer for ${targetId}, abort`);
            return;
          }
          await pc.setLocalDescription(offer);
          socketRef.current?.emit('webrtc_offer', {
            roomId, targetId,
            offer: { type: offer.type, sdp: offer.sdp },
          });
          console.log(`[voice] 📤 Offer sent to ${targetId}`);
        } catch (err) {
          console.error(`[voice] Offer error for ${targetId}:`, err);
        } finally {
          negotiatingRef.current[targetId] = false;
        }
      };
      // ─────────────────────────────────────────────────────────────────────
    }

    peersRef.current[targetId] = pc;
    return pc;
  }, [roomId, socketRef, restartIceForPeer]);

  const joinVoice = useCallback(async () => {
    try {
      console.log('[voice] 🎤 Joining voice chat...');
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 48000,
          channelCount: 1,
        },
      });
      localStreamRef.current = stream;
      inVoiceRef.current = true;
      setInVoice(true);
      setMuted(false);
      console.log('[voice] ✅ Microphone access granted');
      console.log('[voice] Audio tracks:', stream.getAudioTracks().length);
      // Загружаем ICE-конфиг (STUN/TURN) до входа: пиры, созданные в ответ на
      // voice_joined, должны сразу получить актуальные серверы.
      await fetchIceConfig();
      socketRef.current?.emit('voice_joined', { roomId });
    } catch (err) {
      console.error('[voice] ❌ Microphone error:', err);
      let msg = 'Не удалось получить доступ к микрофону\n\n';
      if (err.name === 'NotAllowedError') msg += 'Разрешите доступ к микрофону в браузере';
      else if (err.name === 'NotFoundError') msg += 'Микрофон не найден. Проверьте подключение';
      else if (err.name === 'NotReadableError') msg += 'Микрофон занят другим приложением';
      else msg += err.message;
      alert(msg);
    }
  }, [roomId, socketRef, fetchIceConfig]);

  const leaveVoice = useCallback(() => {
    console.log('[voice] 🚪 Leaving voice chat');
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => track.stop());
      localStreamRef.current = null;
    }
    Object.entries(peersRef.current).forEach(([id, pc]) => {
      pc.close();
      console.log(`[voice] Closed peer to ${id}`);
    });
    peersRef.current = {};
    iceCandidateBufferRef.current = {};
    negotiatingRef.current = {};
    peerRolesRef.current = {};
    reconnectAttemptsRef.current = {};
    Object.entries(audioRefs.current).forEach(([, audio]) => {
      audio.srcObject = null;
      audio.pause();
    });
    audioRefs.current = {};
    inVoiceRef.current = false;
    setInVoice(false);
    setVoiceUsers({});
    setVolumes({});
    volumesRef.current = {};
    setMuted(false);
    socketRef.current?.emit('voice_left', { roomId });
  }, [roomId, socketRef]);

  const toggleMute = useCallback(() => {
    if (localStreamRef.current) {
      const tracks = localStreamRef.current.getAudioTracks();
      if (tracks.length > 0) {
        // track.enabled=true → микрофон включён (не замьючен)
        // Хотим замьютить → currentlyMuted = !tracks[0].enabled → нужно инвертировать enabled
        const currentlyMuted = !tracks[0].enabled;
        const newMuted = !currentlyMuted; // то, чем хотим стать
        tracks.forEach(track => { track.enabled = !newMuted; }); // enabled=false когда замьючен
        setMuted(newMuted);
        console.log(`[voice] 🎙️ Microphone ${newMuted ? 'MUTED' : 'UNMUTED'}`);
      }
    }
  }, []);

  const setUserVolume = useCallback((userId, volume) => {
    const v = Math.max(0, Math.min(1, volume));
    volumesRef.current[userId] = v;
    if (audioRefs.current[userId]) audioRefs.current[userId].volume = v;
    setVolumes(prev => ({ ...prev, [userId]: v }));
    console.log(`[voice] Volume for ${userId} set to ${v * 100}%`);
  }, []);

  useEffect(() => {
    const socket = socketRef.current;
    if (!socket) return;

    // Добавить пира в список голосового чата (с дефолтной громкостью), не затирая
    // уже выставленную пользователем громкость. Источник истины — create_peer (он
    // приходит ОБЕИМ сторонам пары), поэтому ползунок появляется у всех, включая
    // того, кто зашёл в голосовой последним.
    const addVoiceUser = (userId, userName) => {
      if (!userId || userId === socket.id) return;
      setVoiceUsers(prev => ({ ...prev, [userId]: { id: userId, name: userName ?? prev[userId]?.name } }));
      if (volumesRef.current[userId] == null) {
        volumesRef.current[userId] = 1;
        setVolumes(prev => (prev[userId] != null ? prev : { ...prev, [userId]: 1 }));
      }
    };

    const onVoiceUserJoined = ({ userId, userName }) => {
      console.log(`[voice] 👤 voice_user_joined ${userId}`);
      if (!inVoiceRef.current) return;
      addVoiceUser(userId, userName);
      // Peer creation is handled by create_peer from server — no initiator logic here
    };

    const onVoiceUserLeft = ({ userId }) => {
      console.log(`[voice] 👋 voice_user_left ${userId}`);
      setVoiceUsers(prev => { const n = { ...prev }; delete n[userId]; return n; });
      setVolumes(prev => { const n = { ...prev }; delete n[userId]; return n; });
      delete volumesRef.current[userId];
      delete iceCandidateBufferRef.current[userId];
      delete negotiatingRef.current[userId];
      delete peerRolesRef.current[userId];
      delete reconnectAttemptsRef.current[userId];
      if (peersRef.current[userId]) {
        peersRef.current[userId].close();
        delete peersRef.current[userId];
      }
      if (audioRefs.current[userId]) {
        audioRefs.current[userId].srcObject = null;
        audioRefs.current[userId].pause();
        delete audioRefs.current[userId];
      }
    };

    const onCreatePeer = ({ peerId, peerName, asInitiator, recreate }) => {
      console.log(`[voice] 📡 create_peer from server: peerId=${peerId} asInitiator=${asInitiator} recreate=${!!recreate}`);
      if (!inVoiceRef.current) return;
      // Показываем пира в списке (ползунок громкости) сразу — даже до установки медиа.
      addVoiceUser(peerId, peerName);
      // При пересборке (voice_repair) принудительно закрываем старое соединение,
      // чтобы createPeer не «пропустил» его как живое.
      if (recreate && peersRef.current[peerId]) {
        try { peersRef.current[peerId].close(); } catch (_) {}
        delete peersRef.current[peerId];
        reconnectAttemptsRef.current[peerId] = 0;
      }
      const tryCreate = (attempt = 0) => {
        if (localStreamRef.current) {
          createPeer(peerId, asInitiator);
        } else if (attempt < 10) {
          setTimeout(() => tryCreate(attempt + 1), 500);
        } else {
          console.warn(`[voice] createPeer(${peerId}) aborted — no localStream after 5s`);
        }
      };
      tryCreate();
    };

    const onOffer = async ({ fromId, offer }) => {
      console.log(`[voice] 📨 Received offer from ${fromId}`);
      if (fromId === socket.id) return;

      const pc = peersRef.current[fromId] ?? createPeer(fromId, false);

      try {
        await pc.setRemoteDescription(new RTCSessionDescription(offer));
        console.log(`[voice] Remote description set for ${fromId}`);
        await flushIceCandidates(fromId);

        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        socket.emit('webrtc_answer', {
          roomId, targetId: fromId,
          answer: { type: answer.type, sdp: answer.sdp },
        });
        console.log(`[voice] 📤 Answer sent to ${fromId}`);
      } catch (err) {
        console.error(`[voice] Error handling offer from ${fromId}:`, err);
      }
    };

    const onAnswer = async ({ fromId, answer }) => {
      const pc = peersRef.current[fromId];
      if (!pc) {
        console.warn(`[voice] No peer for ${fromId} when receiving answer`);
        return;
      }
      if (pc.signalingState !== 'have-local-offer') {
        console.log(`[voice] Unexpected signalingState for ${fromId}: ${pc.signalingState}, skip answer`);
        return;
      }
      try {
        await pc.setRemoteDescription(new RTCSessionDescription(answer));
        await flushIceCandidates(fromId);
        console.log(`[voice] ✅ Answer applied from ${fromId}`);
      } catch (err) {
        console.error(`[voice] Error handling answer from ${fromId}:`, err);
      }
    };

    const onIce = async ({ fromId, candidate }) => {
      if (!candidate) return;
      const pc = peersRef.current[fromId];

      // Буферизуем кандидата если remote description ещё не установлен
      if (!pc || !pc.remoteDescription) {
        if (!iceCandidateBufferRef.current[fromId]) {
          iceCandidateBufferRef.current[fromId] = [];
        }
        console.log(`[voice] Buffering ICE candidate for ${fromId} (no remote desc yet)`);
        iceCandidateBufferRef.current[fromId].push(candidate);
        return;
      }

      try {
        await pc.addIceCandidate(new RTCIceCandidate(candidate));
        console.log(`[voice] ✅ ICE candidate added for ${fromId}`);
      } catch (err) {
        console.warn(`[voice] Error adding ICE candidate for ${fromId}:`, err);
      }
    };

    socket.on('voice_user_joined', onVoiceUserJoined);
    socket.on('voice_user_left', onVoiceUserLeft);
    socket.on('create_peer', onCreatePeer);
    socket.on('webrtc_offer', onOffer);
    socket.on('webrtc_answer', onAnswer);
    socket.on('webrtc_ice', onIce);

    return () => {
      socket.off('voice_user_joined', onVoiceUserJoined);
      socket.off('voice_user_left', onVoiceUserLeft);
      socket.off('create_peer', onCreatePeer);
      socket.off('webrtc_offer', onOffer);
      socket.off('webrtc_answer', onAnswer);
      socket.off('webrtc_ice', onIce);
    };
  }, [createPeer, flushIceCandidates, roomId, socketRef]);

  useEffect(() => {
    return () => {
      if (inVoiceRef.current) leaveVoice();
    };
  }, [leaveVoice]);

  return {
    inVoice,
    voiceUsers,
    muted,
    volumes,
    joinVoice,
    leaveVoice,
    toggleMute,
    setUserVolume,
  };
}